/**
 * Platform meta constants for SocialAssetEditor.
 * Exported separately so callers can import only what they need without
 * pulling in the full component.
 */

export interface CopyField {
  key: string;
  label: string;
  multiline: boolean;
  limit?: number;
}

/**
 * Per-platform copy field definitions.
 * Accepts both prefixed keys (social_linkedin) and raw keys (linkedin).
 */
export const PLATFORM_COPY_FIELDS: Record<string, CopyField[]> = {
  // Prefixed variants (Campaigns)
  social_linkedin: [
    { key: 'intro_text', label: 'Post Text', multiline: true, limit: 3000 },
  ],
  social_twitter: [
    { key: 'tweet_text', label: 'Tweet', multiline: true, limit: 280 },
  ],
  social_instagram: [
    { key: 'caption', label: 'Caption', multiline: true, limit: 2200 },
  ],
  social_facebook: [
    { key: 'post_text', label: 'Post Text', multiline: true, limit: 500 },
  ],
  // Raw variants (MultiChannel / Catalogue)
  linkedin: [
    { key: 'intro_text', label: 'Post Text', multiline: true, limit: 3000 },
  ],
  twitter: [
    { key: 'tweet_text', label: 'Tweet', multiline: true, limit: 280 },
  ],
  instagram: [
    { key: 'caption', label: 'Caption', multiline: true, limit: 2200 },
  ],
  facebook: [
    { key: 'post_text', label: 'Post Text', multiline: true, limit: 500 },
  ],
};

/**
 * Resolve the initial value of each copy field from the editor's raw
 * `initialContent` + its parsed form. Single source of truth for BOTH the
 * editable field state and the image-seed text — never read `parsedContent`
 * by field key directly elsewhere (its plain-string fallback is keyed
 * `post_text` regardless of platform).
 */
export function resolveInitialFieldValues(
  initialContent: string | Record<string, string> | null | undefined,
  parsedContent: Record<string, string>,
  fields: CopyField[],
): Record<string, string> {
  const initial: Record<string, string> = {};
  fields.forEach(f => {
    const v = parsedContent[f.key];
    if (v !== undefined) {
      initial[f.key] = String(v);
    } else if (typeof initialContent === 'string' && fields.length === 1) {
      initial[f.key] = initialContent;
    } else {
      initial[f.key] = '';
    }
  });
  return initial;
}

/**
 * Platform badge background colours.
 * These are intentionally hardcoded hex values — they represent brand identity
 * colours for external platforms and must not be CSS-variablised.
 */
export const PLATFORM_BADGE_COLOR: Record<string, string> = {
  social_linkedin: '#0a66c2',
  social_twitter: '#000000',
  social_instagram: '#e1306c',
  social_facebook: '#1877f2',
  linkedin: '#0a66c2',
  twitter: '#000000',
  instagram: '#e1306c',
  facebook: '#1877f2',
};

/**
 * Human-readable platform labels used when `platformLabel` prop is not provided.
 */
export const PLATFORM_LABEL_DEFAULTS: Record<string, string> = {
  social_linkedin: 'LinkedIn',
  social_twitter: 'Twitter / X',
  social_instagram: 'Instagram',
  social_facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'Twitter / X',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

